import { useState, type ReactNode } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { classifyToken, COMPARISON_OPERATORS, OPERATORS, toStringLiteral, unescapeString } from '@/lib/formula'

interface Props {
  token: string
  isSelected: boolean
  onOpenChange: (open: boolean) => void
  onReplace: (newToken: string) => void
  onRemove: () => void
  /** Host-owned background/text classes for this token's kind (ref / string /
   *  number / operator / broken-ref, etc.) — each builder already computes
   *  this from its own domain (form fields vs table cells vs placeholders). */
  className: string
  /** The resolved label to show (a field's label, "⚠ deleted field", the raw
   *  literal text, the display symbol for an operator, …). */
  children: ReactNode
  /**
   * Replace-editor for a `ref` token — differs per builder (a form-field
   * picker, a column+row picker, or a placeholder-name input), so the host
   * supplies it. Receives a `replace` callback to call once a new ref token
   * string (already wrapped as `{...}`) is chosen. Omit for non-ref chips.
   */
  refEditor?: (replace: (newToken: string) => void) => ReactNode
}

const OP_KIND_LABEL: Record<string, string> = {
  func: 'Function keyword',
  lparen: 'Opening bracket',
  rparen: 'Closing bracket',
  comma: 'Argument separator',
}

/**
 * One clickable formula chip. Click opens a popover with the editor
 * appropriate to the token's kind (field picker / operator menu / value
 * entry) so it can be replaced in place, plus a Remove action available for
 * every kind — including tokens with no replace editor (parens, commas,
 * function keywords), which can still be deleted on their own.
 */
export function FormulaTokenChip({
  token,
  isSelected,
  onOpenChange,
  onReplace,
  onRemove,
  className,
  children,
  refEditor,
}: Props) {
  const kind = classifyToken(token)
  const [draft, setDraft] = useState(() => (kind.type === 'num' ? token : kind.type === 'str' ? unescapeString(token) : ''))

  const commitDraft = () => {
    if (kind.type === 'num') {
      const t = draft.trim()
      if (t !== '' && Number.isFinite(Number(t))) onReplace(t)
    } else if (kind.type === 'str') {
      onReplace(toStringLiteral(draft))
    }
  }

  return (
    <Popover
      open={isSelected}
      onOpenChange={open => {
        if (open) setDraft(kind.type === 'num' ? token : kind.type === 'str' ? unescapeString(token) : '')
        onOpenChange(open)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          className={cn(
            'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium transition-shadow',
            className,
            isSelected && 'ring-2 ring-primary ring-offset-1',
          )}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 space-y-2' align='start'>
        {kind.type === 'ref' && refEditor && (
          <div className='space-y-1.5'>
            <p className='text-xs font-medium text-muted-foreground'>Replace with</p>
            {refEditor(newToken => {
              onReplace(newToken)
              onOpenChange(false)
            })}
          </div>
        )}

        {(kind.type === 'num' || kind.type === 'str') && (
          <div className='space-y-1.5'>
            <p className='text-xs font-medium text-muted-foreground'>
              {kind.type === 'num' ? 'Number value' : 'Text value'}
            </p>
            <div className='flex items-center gap-1.5'>
              <Input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                className='h-8 text-sm'
                onKeyDown={e => {
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  commitDraft()
                  onOpenChange(false)
                }}
              />
              <Button
                type='button'
                size='sm'
                onClick={() => {
                  commitDraft()
                  onOpenChange(false)
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {(kind.type === 'op' || kind.type === 'cmp') && (
          <div className='space-y-1.5'>
            <p className='text-xs font-medium text-muted-foreground'>Replace operator</p>
            <div className='flex flex-wrap gap-1'>
              {(kind.type === 'op' ? OPERATORS : COMPARISON_OPERATORS).map(op => (
                <Button
                  key={op.token}
                  type='button'
                  size='icon'
                  variant='outline'
                  className='h-7 w-7 text-sm'
                  title={op.label}
                  onClick={() => {
                    onReplace(op.token)
                    onOpenChange(false)
                  }}
                >
                  {op.symbol}
                </Button>
              ))}
            </div>
          </div>
        )}

        {kind.type === 'logic' && (
          <div className='space-y-1.5'>
            <p className='text-xs font-medium text-muted-foreground'>Replace logical operator</p>
            <div className='flex gap-1'>
              {['AND', 'OR'].map(v => (
                <Button
                  key={v}
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => {
                    onReplace(v)
                    onOpenChange(false)
                  }}
                >
                  {v}
                </Button>
              ))}
            </div>
          </div>
        )}

        {(kind.type === 'func' || kind.type === 'lparen' || kind.type === 'rparen' || kind.type === 'comma') && (
          <p className='text-xs text-muted-foreground'>
            {OP_KIND_LABEL[kind.type]} — part of a structured expression built with the buttons
            below. It can be removed on its own, but not swapped for another token here.
          </p>
        )}

        <div className='flex justify-end border-t pt-1.5'>
          <Button
            type='button'
            size='sm'
            variant='ghost'
            className='h-7 gap-1 px-2 text-xs text-destructive hover:text-destructive'
            onClick={() => {
              onRemove()
              onOpenChange(false)
            }}
          >
            <Trash2 className='h-3.5 w-3.5' />
            Remove this token
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
